<template>
  <div class="help">
    <div class="d-flex mb-3 justify-content-between position-relative">
      <h1 class="h4 primary--text">
        Help
      </h1>
    </div>
    <main class="help__main">
      <div class="d-flex primary--text">
        <router-link
          :to="`/content/add/help`"
          :disabled="loading"
        >
          {{ $t('views.help.add') }}
        </router-link>
      </div>
      <v-treeview
        v-if="help"
        :items="help"
        :open.sync="open"
        :load-children="getHelp"
        item-key="id"
      >
        <template v-slot:prepend="{ item, open }">
          <v-icon
            v-if="item.type === 'node'"
          >
            {{ open ? 'mdi-folder-open-outline' : 'mdi-folder-outline' }}
          </v-icon>
          <v-icon v-else>
            {{ icons[item.type] }}
          </v-icon>
        </template>
        <template v-slot:label="{ item }">
          <draggable
            :id="item.id"
            :list="help"
            group="node"
            :data-parent="item.parent"
            :disabled="loading"
            @end="checkEnd"
          >
            <div class="d-flex">
              <div class="primary--text mr-auto">
                <router-link
                  :to="`/content/page/${item.id}/help`"
                  :disabled="loading"
                >
                  {{ item.lead }}
                </router-link>
              </div>
              <div class="help__icons">
                <v-icon
                  v-if="item.type === 'node'"
                  class="mr-2"
                  color="primary"
                  size="20"
                  @click="addItem(item)"
                >
                  mdi-plus
                </v-icon>
                <v-icon
                  v-if="item.type !== 'node' || (item.type === 'node' && !item.children.length)"
                  class="mr-2"
                  color="primary"
                  size="20"
                  @click="removeItem(item)"
                >
                  mdi-delete
                </v-icon>
              </div>
            </div>
          </draggable>
        </template>
      </v-treeview>
    </main>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { clone } from 'lodash-es'; // eslint-disable-line
import { mapGetters } from 'vuex';

export default {
  name: 'HelpPage',
  components: {
    draggable,
  },
  props: {
    id: String,
    type: String,
  },
  data: () => ({
    loading: false,
    icons: {
      link: 'mdi-search-web',
      image: 'mdi-file-image-outline',
      content: 'mdi-code-tags',
    },
    open: [],
    help: [],
    draggedItem: null,
  }),
  computed: {
    ...mapGetters(['business']),
  },
  created() {
    this.getHelp();
  },
  methods: {
    async getHelp(item) {
      this.loading = true;
      const params = {
        query: {
          contentType: 'Help',
          parent: item?.id || '',
        },
        populate: 'image',
        pageSize: 1000000,
      };

      // if (this.filter === 1) {
      //   params.query.isPublished = false;
      // }

      const { data } = await this.$http.get('/content', { params }).finally(() => {
        this.loading = false;
      });

      const help = data.rows.map((it) => ({
        ...it, type: it.content ? 'content' : it.link ? 'link' : it.image.filePath ? 'image' : 'node', // eslint-disable-line
        children: (it.content || it.link || it.image.filePath) ? undefined : [],
      }));

      if (!item) {
        this.help = help;
      } else {
        item.children = help;
      }
    },
    async removeItem(item) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      await this.$http.delete(`/content/${item.id}`).finally(() => {
        this.loading = false;
      });

      const removeById = (arr, targetId) => arr.reduce((acc, obj) => ((obj.id === targetId)
        ? acc
        : [...acc,
          {
            ...obj,
            ...(obj.children && { children: removeById(obj.children, targetId) }),
          },
        ]),
      []);
      this.help = removeById(this.help, item.id);
    },
    addItem(item) {
      this.$router.push(`/content/add/help/${item.id}`);
    },
    async checkEnd(evt) {
      const draggedItemId = evt.from.id;
      const draggedItem = this.findTreeItem(this.help, draggedItemId);
      const draggedItemParentId = evt.from.dataset.parent;
      const toParentId = evt.to.dataset.parent;

      const fromParent = draggedItemParentId ? this.findTreeItem(this.help, draggedItemParentId) : null;
      const toParent = this.findTreeItem(this.help, toParentId);
      const objFrom = fromParent ? fromParent.children : this.help;

      const params = clone(draggedItem);

      if (toParentId === '') {
        return;
      }

      objFrom.splice(objFrom.indexOf(draggedItem), 1);
      draggedItem.parent = toParentId;
      toParent.children.push(draggedItem);

      if (draggedItemParentId === toParentId) {
        return;
      }

      delete params.children;
      delete params.type;
      delete params.contentType;
      delete params.date;
      params.parent = toParentId;

      this.loading = true;
      await this.$http.put(`/content/${draggedItemId}`, params).finally(() => {
        this.loading = false;
      });
    },
    findTreeItem(items, id) {
      if (!items) {
        return;
      }
      for (let i = 0; i < items.length; i++) { //eslint-disable-line
        const item = items[i];
        if (item.id === id) {
          return item; //eslint-disable-line
        }
        const child = this.findTreeItem(item.children, id);
        if (child) {
          return child; //eslint-disable-line
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.help {
  &__back {
    display: flex;
    align-items: center;
    color: var(--aurum);
    transition: color 0.3s ease-in-out;

    svg {
      transform: rotate(180deg);
      margin-right: 5px;
      fill: var(--aurum);
      transition: fill 0.3s ease-in-out;
    }

    &:hover {
      color: var(--margaritas);
      text-decoration: none;

      svg {
        fill: var(--margaritas);
      }
    }
  }

  // &__icons {
  //   width: 56px;
  // }

  &__main {
    background-color: #fff;
    min-height: calc(100vh - 215px);
    padding: 30px;
  }

  &__wrapper {
    display: grid;
    grid-template-columns: repeat(2, auto);
    column-gap: 20px;
    align-items: center;
  }

  &__edit-button {
    padding: 12px 25px;
    margin: 0px 10px 0px 10px;

    svg {
      fill: var(--black);
      margin-right: 7px;
      transition: fill 0.3s ease-in-out;
    }

    &:hover {
      svg {
        fill: var(--aurum);
      }
    }

    &:active {
      svg {
        fill: #9d6d4c;
      }
    }
  }
}
</style>
